import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  Link,
  Icon,
  Divider,
  useColorMode,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { FaGraduationCap, FaBook, FaQuoteLeft, FaUniversity, FaAward } from "react-icons/fa";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../brandConfig";
import { SEOHead } from "./seometadata/components/SEOHead";

interface Publication {
  year: number;
  title: string;
  authors: string;
  venue: string;
  downloads?: number;
  scopusCitations?: number;
  wosCitations?: number;
  link?: string;
  isThesis?: boolean;
}

const publications: Publication[] = [
  {
    year: 2025,
    title: "Decentralising Trust and Information Symmetry: Seeking Retrospective Common Knowledge in Blockchain Enabled Supply Chains",
    authors: "Miller, Thomas",
    venue: "PhD by Publication, Queensland University of Technology",
    downloads: 1,
    isThesis: true,
  },
  {
    year: 2025,
    title: "Proofs of Information Symmetry for Meeting of the Minds in E-Commerce Transactions",
    authors: "Miller, Thomas, Boyen, Xavier, Cao, Shoufeng, Foth, Marcus, & Powell, Warwick",
    venue: "Distributed Ledger Technologies: Research and Practice (DLT)",
    downloads: 16,
  },
  {
    year: 2025,
    title: "Integrating the REA model and multisig architecture for blockchain-based supply chain traceability: An organisational-technical approach",
    authors: "Cao, Shoufeng, Miller, Thomas, Foth, Marcus, & Powell, Warwick",
    venue: "Technological Forecasting and Social Change, 219, Article number: 124297",
    downloads: 32,
  },
  {
    year: 2023,
    title: "An asset-backed decentralised finance instrument for food supply chains - A case study from the livestock export industry",
    authors: "Miller, Thomas, Cao, Shoufeng, Foth, Marcus, Boyen, Xavier, & Powell, Warwick",
    venue: "Computers in Industry, 147, Article number: 103863",
    downloads: 158,
    scopusCitations: 28,
    wosCitations: 16,
  },
  {
    year: 2023,
    title: "Enabling cross-border trade in the face of regulatory barriers to data flow - the case of the blockchain-based service network",
    authors: "Cao, Shoufeng, Boyen, Xavier, Deane, Felicity, Miller, Thomas, & Foth, Marcus",
    venue: "Blockchain for Industry 4.0: Emergence, Challenges and Opportunities. CRC Press",
    downloads: 180,
    scopusCitations: 6,
  },
  {
    year: 2022,
    title: "A Blockchain-based Multisignature Approach for Supply Chain Governance: A Use Case from the Australian Beef Industry",
    authors: "Cao, Shoufeng, Foth, Marcus, Powell, Warwick, Miller, Thomas, & Li, Ming",
    venue: "Blockchain: Research and Applications, 3(4), Article number: 100091",
    downloads: 183,
    scopusCitations: 43,
    wosCitations: 25,
  },
  {
    year: 2021,
    title: "From premise to practice of social consensus: How to agree on common knowledge in blockchain-enabled supply chains",
    authors: "Powell, Warwick, Cao, Shoufeng, Miller, Thomas, Foth, Marcus, Boyen, Xavier, Earsman, Barry, Del Valle Gallo, Santiago, & Turner-Morris, Charles",
    venue: "Computer Networks, 200, Article number: 108536",
    downloads: 213,
    scopusCitations: 22,
    wosCitations: 14,
  },
  {
    year: 2021,
    title: "Integrating On-chain and Off-chain Governance for Supply Chain Transparency and Integrity",
    authors: "Cao, Shoufeng, Miller, Thomas, Foth, Marcus, Powell, Warwick, Boyen, Xavier, & Turner-Morris, Charles",
    venue: "Proceedings of the 5th Symposium on Distributed Ledger Technology",
    downloads: 81,
  },
  {
    year: 2021,
    title: "Strengthening consumer trust in beef supply chain traceability with a blockchain-based human-machine reconcile mechanism",
    authors: "Cao, Shoufeng, Powell, Warwick, Foth, Marcus, Natanelov, Valeri, Miller, Thomas, & Dulleck, Uwe",
    venue: "Computers and Electronics in Agriculture, 180, Article number: 105886",
    downloads: 503,
    scopusCitations: 136,
    wosCitations: 100,
  },
];

const Doctorate = () => {
  const { colorMode } = useColorMode();

  const bg = getColor("background.main", colorMode);
  const cardBg = colorMode === 'light' ? "white" : "gray.800";
  const cardBorder = colorMode === 'light' ? "gray.200" : "gray.700";
  const textPrimary = getColor(colorMode === 'light' ? "text.primary" : "text.primaryDark", colorMode);
  const textSecondary = getColor(colorMode === 'light' ? "text.secondary" : "text.secondaryDark", colorMode);
  const accentBlue = getColor("primary", colorMode);

  const totalCitations = publications.reduce((acc, pub) => acc + (pub.scopusCitations || 0), 0);
  const totalDownloads = publications.reduce((acc, pub) => acc + (pub.downloads || 0), 0);

  return (
    <Box bg={bg} minHeight="100vh">
      <SEOHead
        pagePath="/doctorate"
        fallbackTitle="Dr. Thomas Miller - PhD in Blockchain Security"
        fallbackDescription="Dr. Thomas Miller has completed his PhD in Blockchain Security at Queensland University of Technology, specialising in decentralised trust and supply chain transparency."
      />
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={16}>
        <VStack spacing={10} align="stretch">
          {/* Header Section */}
          <Box textAlign="center">
            <Icon as={FaGraduationCap} boxSize={16} color={accentBlue} mb={4} />
            <Heading as="h1" size="2xl" color={textPrimary} mb={4}>
              Dr. Thomas Miller
            </Heading>
            <Text fontSize="xl" color={textSecondary} mb={2}>
              PhD in Blockchain Security
            </Text>
            <HStack justify="center" spacing={2}>
              <Icon as={FaUniversity} color={accentBlue} />
              <Text fontSize="lg" color={textSecondary}>
                Queensland University of Technology, Brisbane, Australia
              </Text>
            </HStack>
          </Box>

          {/* Thesis Summary */}
          <Card bg={cardBg} borderColor={cardBorder} borderWidth="1px" shadow="lg">
            <CardBody>
              <HStack mb={4}>
                <Icon as={FaAward} color={accentBlue} boxSize={6} />
                <Heading as="h2" size="lg" color={textPrimary}>
                  Doctoral Thesis
                </Heading>
              </HStack>
              <Heading as="h3" size="md" color={accentBlue} mb={4}>
                Decentralising Trust and Information Symmetry: Seeking Retrospective Common Knowledge in Blockchain Enabled Supply Chains
              </Heading>
              <Box bg={colorMode === 'light' ? "blue.50" : "blue.900"} p={4} borderRadius="md" mb={4}>
                <HStack align="start">
                  <Icon as={FaQuoteLeft} color={accentBlue} mt={1} />
                  <Text color={textSecondary} fontStyle="italic">
                    This research explores how blockchain technology can establish transparent, accountable systems
                    that enhance trust in supply chains. By addressing information asymmetry and creating mechanisms
                    for retrospective common knowledge, the work demonstrates how decentralised systems can foster
                    human connection and cooperation rather than control.
                  </Text>
                </HStack>
              </Box>
              <Text color={textSecondary}>
                The thesis, completed as a PhD by Publication, represents over a decade of research and practical
                experience in blockchain development, combining academic rigour with real-world applications in
                industries including Australian beef exports and cross-border trade.
              </Text>
            </CardBody>
          </Card>

          {/* Impact Statistics */}
          <HStack spacing={6} justify="center" wrap="wrap">
            <Card bg={cardBg} borderColor={cardBorder} borderWidth="1px" p={6} textAlign="center" minW="150px">
              <Text fontSize="3xl" fontWeight="bold" color={accentBlue}>{publications.length}</Text>
              <Text color={textSecondary}>Publications</Text>
            </Card>
            <Card bg={cardBg} borderColor={cardBorder} borderWidth="1px" p={6} textAlign="center" minW="150px">
              <Text fontSize="3xl" fontWeight="bold" color={accentBlue}>{totalCitations}+</Text>
              <Text color={textSecondary}>Citations</Text>
            </Card>
            <Card bg={cardBg} borderColor={cardBorder} borderWidth="1px" p={6} textAlign="center" minW="150px">
              <Text fontSize="3xl" fontWeight="bold" color={accentBlue}>{totalDownloads.toLocaleString()}+</Text>
              <Text color={textSecondary}>Downloads</Text>
            </Card>
          </HStack>

          {/* Publications List */}
          <Box>
            <HStack mb={6}>
              <Icon as={FaBook} color={accentBlue} boxSize={6} />
              <Heading as="h2" size="lg" color={textPrimary}>
                Publications
              </Heading>
            </HStack>

            <VStack spacing={4} align="stretch">
              {publications.map((pub, index) => (
                <Card
                  key={index}
                  bg={cardBg}
                  borderColor={pub.isThesis ? accentBlue : cardBorder}
                  borderWidth={pub.isThesis ? "2px" : "1px"}
                  shadow={pub.isThesis ? "md" : "sm"}
                >
                  <CardBody>
                    <HStack justify="space-between" mb={2} wrap="wrap">
                      <Badge colorScheme="blue" fontSize="sm">{pub.year}</Badge>
                      {pub.isThesis && (
                        <Badge colorScheme="green" fontSize="sm">
                          <Icon as={FaGraduationCap} mr={1} />
                          PhD Thesis
                        </Badge>
                      )}
                    </HStack>
                    <Heading as="h3" size="sm" color={textPrimary} mb={2}>
                      {pub.title}
                    </Heading>
                    <Text fontSize="sm" color={textSecondary} mb={1}>
                      {pub.authors}
                    </Text>
                    <Text fontSize="sm" color={textSecondary} fontStyle="italic" mb={3}>
                      {pub.venue}
                    </Text>
                    <HStack spacing={4} wrap="wrap" fontSize="xs" color={textSecondary}>
                      {pub.downloads && (
                        <Text>{pub.downloads} downloads</Text>
                      )}
                      {pub.scopusCitations && (
                        <Text>{pub.scopusCitations} Scopus citations</Text>
                      )}
                      {pub.wosCitations && (
                        <Text>{pub.wosCitations} Web of Science citations</Text>
                      )}
                    </HStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          </Box>

          {/* Link to QUT Profile */}
          <Box textAlign="center" py={6}>
            <Divider mb={6} />
            <Link
              href="https://eprints.qut.edu.au/view/person/Miller,_Thomas.html"
              isExternal
              color={accentBlue}
              fontSize="lg"
              fontWeight="medium"
            >
              View full publication list on QUT ePrints <ExternalLinkIcon mx="2px" />
            </Link>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Doctorate;
